import * as React from 'react';
import {connect} from 'react-redux';
import {WithStyles, withStyles, MenuItem, Menu, Divider, ListItemIcon,  IconButton, Dialog, DialogActions, DialogTitle, } from '@material-ui/core';
import Button from '@components/common/button/Button';
import TranslateNew from '@translate/TranslateNew';
import { HeaderStyle } from './Header.style';
import { ISuggestion, IScheduleChange, ICreateSuggestion } from '@app-types/websocket/suggestion.types';
import { CalendarEditIcon } from '@icons/CalendarEdit';
import BackIcon from "@material-ui/icons/ArrowBackIos";
import AddIcon from "@material-ui/icons/AddRounded";
import CloseIcon from '@material-ui/icons/CloseRounded';
import ActivateIcon from '@material-ui/icons/EventAvailable';
import ShareIcon from '@material-ui/icons/VisibilityOff';
import { ICalendarSetting } from '@app-types/websocket/user.types';
import { SUGGESTION_TYPES, SUGGESTION_CHANGE_TYPES } from '@constants/suggestionTypes';
import SuggestionItem from './SuggestionItem';
import { IFamilyMember } from '@app-types/websocket/family.types';
import { grayColor, primaryColor } from 'src/main.style';
import { ISchedule } from '@app-types/websocket/schedule.types';
import * as UserActions from '@services/user/user.actions';
import * as Suggestion from '@services/suggestion/suggestion.action';
import Guide from '@components/guide/guide.component';
import { DialogTop } from '@components/economy/DialogComponens';
import SCHEDULE_TYPES from '@constants/scheduleTypes';

const tn: TranslateNew = TranslateNew.getInstance();

interface IState {
  open:boolean;
  createNewSchedule:boolean;
  editSuggestionInstead:{suggestionId:number, scheduleId:number}|undefined;
}

interface IProps extends WithStyles<typeof HeaderStyle> {
  userId:number;
  family:IFamilyMember[];
  suggestions:ISuggestion[];
  schedules:ISchedule[];
  isEditing:boolean;
  calendarSettings: ICalendarSetting;
  startEditing:(scheduleId:number, suggestionId:number|undefined) => void;
  cancelEdit:() => void;
  showDetails:(suggestion:ISuggestion) => void;
  updateCalendarSettings: (data: ICalendarSetting) => void;
  createSuggestion: (data: ICreateSuggestion) => void;
}

class MainMenu extends React.Component<IProps, IState> {

  private buttonRef:React.RefObject<HTMLButtonElement>=React.createRef();

  constructor(props: IProps) {
    super(props);

    this.state = {
      open:false,
      createNewSchedule:false,
      editSuggestionInstead:undefined,
    };

  }

  public render() {
    const {classes, isEditing, suggestions, userId, family, startEditing, calendarSettings, schedules} = this.props;

    let activeCalendars:number[] = [];
    if (this.props.calendarSettings && this.props.calendarSettings.calendars) {
      activeCalendars = this.props.calendarSettings.calendars;
    }

    let totalSentSuggestions = 0;
    let totalReceivedSuggestions = 0;

    let suggestionsAwaitingReply:ISuggestion[] = [];
    let suggestionsOld:ISuggestion[] = [];
    if ( suggestions.length > 0 ) {
      suggestionsOld = suggestions.filter((sugg) =>(
        activeCalendars.includes(sugg.schedules.length > 0?sugg.schedules[0]:-100)
          && ((sugg.status === SUGGESTION_TYPES.ACCEPTED_AND_EXECUTED && sugg.responseAccountId !== 0)
          || sugg.status === SUGGESTION_TYPES.DELETED
          || sugg.status === SUGGESTION_TYPES.REJECTED)))
          .sort((a,b) => b.responseTime - a.responseTime)
          .slice(0,5);
      
      suggestionsAwaitingReply = suggestions.filter((sugg) =>(
        ((sugg.status === SUGGESTION_TYPES.AWAITING_APPROVAL ) 
          && sugg.changes.findIndex((c) => c.type === SUGGESTION_CHANGE_TYPES.DELETE_SCHEDULE) > -1)
        || (activeCalendars.includes(sugg.schedules.length > 0?sugg.schedules[0]:-100)
          && (sugg.status === SUGGESTION_TYPES.AWAITING_APPROVAL ))))
        .sort((a,b) => b.publishTime - a.publishTime);

      totalSentSuggestions = suggestionsAwaitingReply.filter((sugg) => sugg.creatorId === userId).length;
      totalReceivedSuggestions = suggestionsAwaitingReply.length - totalSentSuggestions;
    }


    const primaryActions:JSX.Element[] = [];

    if (calendarSettings === undefined) {
      return <div />;
    } else if (calendarSettings.calendars === undefined || calendarSettings.calendars.length === 0) {
      if (schedules.filter((s) => (s.status !== SCHEDULE_TYPES.DELETED && s.status !== SCHEDULE_TYPES.DELETED_WIP)).length === 0) {
        /* create a schedule */
        primaryActions.push( 
          <MenuItem key = {"new"}
            className={classes.menuListItem}
            onClick={() => {this.setState({createNewSchedule:true}); this.close();}}>
            <ListItemIcon>
              <AddIcon />
            </ListItemIcon>          
            <div className={classes.menuItemTitle}>
              {tn.lang.newCalendar}
            </div>
          </MenuItem>
        );
      } else {
        /* show one of the schedules */
        for (const schedule of schedules) {
          if (schedule.status === SCHEDULE_TYPES.SHARED || schedule.status === SCHEDULE_TYPES.ACTIVE) {
            primaryActions.push( 
              <MenuItem key = {"a"+schedule.id}
                className={classes.menuListItem}
                onClick={() => {this.activateSchedule(schedule.id); this.close();}}>
                <ListItemIcon>
                  <ActivateIcon />
                </ListItemIcon>          
                <div className={classes.menuItemTitle} style={{ flexDirection: 'column',alignItems: 'flex-start'}}>
                  <div style={{fontWeight:500}}>{tn.lang.scheduleNameNew(schedule)}</div>
                  <div>{tn.lang.calMakeActive}</div>
                </div>
              </MenuItem>
            );
          }
        }
      }
    } else if (calendarSettings.calendars.length === 1) {
      /* edit */
      const calendarId = calendarSettings.calendars[0];
      primaryActions.push( 
        <MenuItem key = {calendarId}
          className={classes.menuListItem}
          onClick={() => this.newSuggestion(calendarId)}>
          <ListItemIcon>
            <AddIcon />
          </ListItemIcon>          
          <div className={classes.menuItemTitle}>
            {tn.lang.calNewSuggestion}
          </div>
        </MenuItem>
      );
      for (const schedule of schedules) {
        if (schedule.id === calendarSettings.calendars![0] && schedule.status === SCHEDULE_TYPES.WIP) {
          primaryActions.push( 
            <MenuItem key = {"s"+schedule.id}
              className={classes.menuListItem}
              onClick={() => {this.shareSchedule(schedule.id); this.close();}}>
              <ListItemIcon>
                <ShareIcon />
              </ListItemIcon>    
              <div className={classes.menuItemTitle}>
                {tn.lang.calShareSchedule}
              </div> 
            </MenuItem>
          );
        }
      }
    } else {
      /* edit one of the schedules */

      for (const schedule of schedules) {
        if (calendarSettings.calendars.includes(schedule.id) && schedule.status === SCHEDULE_TYPES.WIP) {
          primaryActions.push( 
            <MenuItem key = {"s"+schedule.id}
              className={classes.menuListItem}
              onClick={() => {this.shareSchedule(schedule.id); this.close();}}>
              <ListItemIcon>
                <ShareIcon />
              </ListItemIcon>
              <div className={classes.menuItemTitle}>
                {tn.lang.calShareSchedule +" "+ tn.lang.scheduleNameNew(schedule)}
              </div>        
            </MenuItem>
          );
        }
        if (calendarSettings.calendars.includes(schedule.id)) {
          primaryActions.push( 
            <MenuItem key = {schedule.id}
              className={classes.menuListItem}
              onClick={() => this.newSuggestion(schedule.id)}>
              <ListItemIcon>
                <AddIcon />
              </ListItemIcon>          
              <div className={classes.menuItemTitle}>
                {tn.lang.edit + " "+ tn.lang.scheduleNameNew(schedule)}
              </div>
            </MenuItem>
          );
        }
      }
    }


    return (<>


      <IconButton style={{marginLeft:'12px'}}
        onClick={() => {
          if (this.props.isEditing) {this.props.cancelEdit()}  
          else {this.setState({open:true});}}} ref={this.buttonRef} >
        {isEditing?<BackIcon style={{fill:"#ffffff"}} />:<CalendarEditIcon style={{opacity: 0.50}}/>}
        <div style={{position:'absolute', display: 'flex', top: '0px', right: '0px'}}> { /* The badges */ }
          <span style={{backgroundColor:grayColor}} className={classes.suggestionBadge
            +((totalSentSuggestions < 1 || isEditing)?(" "+classes.hiddenBadge):"")}>
              {totalSentSuggestions}
          </span>
          <span style={{backgroundColor:primaryColor}} className={classes.suggestionBadge
            +((totalReceivedSuggestions < 1 || isEditing)?(" "+classes.hiddenBadge):"")}>
              {totalReceivedSuggestions}
          </span>
        </div>
      </IconButton>

      <Menu
        anchorEl={this.buttonRef.current}
        open={this.state.open}
        anchorReference="anchorEl"
        onClose={() => this.setState({open:false})}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        classes={{
          paper: classes.menuPaper,
          list: classes.menuList,
        }}
      >

        {primaryActions}


        {/* unanswered suggestions */}
        {suggestionsAwaitingReply.length >0 && <Divider />}
        {suggestionsAwaitingReply.map((sugg) =>
          <SuggestionItem key={""+sugg.id} suggestion={sugg} userId={userId} family={family}
            showDetails={(suggestion:ISuggestion) => {this.props.showDetails(suggestion)}}
            close={() => this.close()} startEditing={startEditing} />)}

        {/* answered suggestions */}
        {suggestionsOld.length >0 && <Divider />}
        {suggestionsOld.map((sugg) =>
          <SuggestionItem key={""+sugg.id} suggestion={sugg} userId={userId} family={family} 
            showDetails={(suggestion:ISuggestion) => {this.props.showDetails(suggestion)}}
            close={() => this.close()} startEditing={startEditing} />)}

      </Menu>
      <Dialog
        open={ this.state.createNewSchedule}
        onClose={() => this.setState({ createNewSchedule:false})}
        classes={{paper:classes.fullscreenPaper}}
      >
        <div className={classes.background}>
          <div className={classes.circle}/>
        </div>
        <Guide
          style={{zIndex:1}}
          isOnboarding={false}
          firstSchedule={false}
          completedCallback={() => this.setState({ createNewSchedule:false})}
          abortCallback={(event) => {this.setState({ createNewSchedule:false}); event.stopPropagation();}}
        />
      </Dialog>
      <Dialog
        open={ this.state.editSuggestionInstead !== undefined }
        onClose={() => this.setState({ editSuggestionInstead:undefined})}
        classes={{paper:this.props.classes.widePaper}}
      >
        <DialogTop>
          <DialogTitle>{tn.lang.continue+"?"}</DialogTitle>
          <IconButton onClick={() => this.setState({ editSuggestionInstead:undefined})}>
            <CloseIcon />
          </IconButton>
        </DialogTop>
        <div style={{padding: '8px 24px', display: 'flex', flexDirection: 'column'}}>
          {tn.lang.calExistingSuggestion}
        </div>
        <DialogActions style={{padding: '24px', paddingTop: '8px'}}>
          <Button  variant='outlined' classes={{outlined:classes.buttonOutlined}} 
            onClick={() => {this.setState({ editSuggestionInstead:undefined}); 
              if (this.state.editSuggestionInstead !== undefined) {
              this.props.startEditing(this.state.editSuggestionInstead.scheduleId, 0);
            }
          }} >
            {tn.lang.createNew}
          </Button>
          <Button  variant='contained' classes={{contained:classes.buttonContained}} color="primary"
            onClick={() => {this.setState({ editSuggestionInstead:undefined}); 
              if (this.state.editSuggestionInstead !== undefined) {
              this.props.startEditing(this.state.editSuggestionInstead.scheduleId, this.state.editSuggestionInstead?.suggestionId);
              }
            }} >
            {tn.lang.updateExisting}
          </Button>
        </DialogActions>
      </Dialog>
      </>
    );
  }

  private close() {
    this.setState({open:false});
  }

  private newSuggestion(scheduleId:number) {
    
    let ongoingSuggestion:number|undefined;
    for (const sugg of this.props.suggestions) {
      if (sugg.creatorId === this.props.userId && sugg.status === SUGGESTION_TYPES.AWAITING_APPROVAL && sugg.schedules.includes(scheduleId)) {
        ongoingSuggestion = sugg.id;
      }
    }
    if (ongoingSuggestion === undefined) {
      this.props.startEditing(scheduleId, 0);
    } else {
      this.setState({editSuggestionInstead:{scheduleId, suggestionId:ongoingSuggestion}});
    }
    this.close();
  }

  private activateSchedule(scheduleId:number,) {
    const calSettings = this.props.calendarSettings;
    if (calSettings) {
      const settingsCopy:ICalendarSetting = JSON.parse(JSON.stringify(calSettings));
      if (settingsCopy.calendars !== undefined && !settingsCopy.calendars.includes(scheduleId)) {
        settingsCopy.calendars.push(scheduleId);
      }
      this.props.updateCalendarSettings(settingsCopy);
    }
  }

  private shareSchedule(scheduleId:number) {
    const mods:IScheduleChange[] = [];
    mods.push({
      scheduleId,
      type: SUGGESTION_CHANGE_TYPES.SHARE,
      isParentA: true,
      newScheduleEntry: undefined,
      oldScheduleEntry: undefined,
      children:undefined
    });

    const data: ICreateSuggestion = {
      changes: mods,
    };

    this.props.createSuggestion(data);
  }
}



function mapStateToProps(state: any) {
  const suggestions:ISuggestion[] = state.suggestion.suggestions?state.suggestion.suggestions:[];
  const calendarSettings: ICalendarSetting = state.user.calendarSettings;
  const userId = state.user.session.data.id;
  

  return {
    userId,
    suggestions,
    calendarSettings,
    family: state.family.family,
    schedules:state.schedule.schedules?state.schedule.schedules:[],
  };
}

const mapDispatchToProps = {
  updateCalendarSettings:UserActions.updateCalendarSettings,
  createSuggestion:Suggestion.createSuggestion,
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(HeaderStyle)(MainMenu));



// WEBPACK FOOTER //
// ./src/components/calendar2/header/EditMenu.tsx